import { SearchDealEntityType, SearchDealQuery } from "@/src/graphql/generated";
import {
  ParentFolder,
  ResultItemHeaderWrapper,
  SearchResultCategory,
  SeparationDot,
} from "./search_result";
import { formatDistanceToNowStrict, fromUnixTime } from "date-fns";
import { FileIcon } from "../../FileIcon";
import { HighlightedText } from "../../HighlightedText";
import { FolderIcon } from "@heroicons/react/24/solid";
import {
  truncateText,
  truncateTextWithSearchTerm,
} from "@/src/utils/truncateText";
import { Avatar } from "../../account/Avatar";
import {
  FilePlusIcon,
  ListIcon,
  MessageCirclePlusIcon,
  MessageCircleQuestionIcon,
} from "lucide-react";

interface Props {
  result: SearchDealQuery["searchDeal"][0]["entity"];
  searchTerm: string;
  isFocused: boolean;
}

export function SearchResultEntity(props: Props) {
  const { result } = props;

  if (!result) {
    return null;
  }

  switch (result.entityType) {
    case SearchDealEntityType.DataRoomFile:
      return <SearchResultFile {...props} />;
    case SearchDealEntityType.DataRoomFolder:
      return <SearchResultFolder {...props} />;
    case SearchDealEntityType.Question:
      return <SearchResultQuestion {...props} />;
    case SearchDealEntityType.Ddql:
      return <SearchResultDdql {...props} />;
    case SearchDealEntityType.QuestionAnswer:
      return <SearchResultQuestionAnswer {...props} />;
    case SearchDealEntityType.QuestionAnswerFile:
      return <SearchResultQuestionAnswerFile {...props} />;
    case SearchDealEntityType.DealAccount:
      return <SearchResultDealAccount {...props} />;
    default:
      return null;
  }
}

function SearchResultFile(props: Props) {
  const { result, searchTerm } = props;

  if (!result) {
    return null;
  }

  if (!result.dataRoomFile) {
    return null;
  }

  const file = result.dataRoomFile!;

  return (
    <div className="flex items-start py-2">
      <SearchResultCategory category="file" />
      <div className="ml-3">
        <ResultItemHeaderWrapper>
          <ParentFolder folder={file.parentFolder} />
          <SeparationDot />
          <p className="text-xs/none text-gray-500">
            Created{" "}
            {formatDistanceToNowStrict(fromUnixTime(file.createdAt), {
              addSuffix: true,
            })}{" "}
            by {file.createdBy.name}
          </p>
        </ResultItemHeaderWrapper>
        <div className=" flex items-center gap-2 ">
          <FileIcon fileType={file.fileType} />
          <HighlightedText
            text={file.name}
            searchTerm={searchTerm}
            className="text-sm text-gray-700 font-semibold"
          />
        </div>
      </div>
    </div>
  );
}

function SearchResultFolder(props: Props) {
  const { result, searchTerm } = props;

  if (!result || !result.dataRoomFolder) {
    return null;
  }

  return (
    <div className="flex items-start py-2">
      <SearchResultCategory category="folder" />
      <div className="ml-3">
        <ResultItemHeaderWrapper>
          <ParentFolder folder={result.dataRoomFolder.parentFolder} />
          <SeparationDot />
          <p className="text-xs/none text-gray-500">
            Created{" "}
            {formatDistanceToNowStrict(
              fromUnixTime(result.dataRoomFolder.createdAt),
              {
                addSuffix: true,
              },
            )}{" "}
            by {result.dataRoomFolder.createdBy.name}
          </p>
        </ResultItemHeaderWrapper>
        <div className="flex items-center gap-2 ">
          <FolderIcon className="w-5 h-5 text-blue-700/70 flex-shrink-0" />
          <HighlightedText
            text={truncateTextWithSearchTerm(
              result.dataRoomFolder.name,
              searchTerm,
              120,
            )}
            searchTerm={searchTerm}
            className="text-sm text-gray-700 font-semibold"
          />
        </div>
      </div>
    </div>
  );
}

function SearchResultQuestion(props: Props) {
  const { result, searchTerm } = props;

  if (!result || !result.question) {
    return null;
  }

  return (
    <div className="flex items-start py-2">
      <SearchResultCategory category="questions" />
      <div className="ml-3">
        <ResultItemHeaderWrapper>
          <div className="items-center flex gap-x-1">
            <MessageCircleQuestionIcon className="w-3 h-3 text-gray-500" />
            <p className="text-xs/none text-gray-500">Question</p>
          </div>
          <SeparationDot />
          <p className="text-xs/none text-gray-500">
            Asked{" "}
            {formatDistanceToNowStrict(
              fromUnixTime(result.question.createdAt),
              {
                addSuffix: true,
              },
            )}{" "}
            by {result.question.createdBy.name}
          </p>
        </ResultItemHeaderWrapper>
        <HighlightedText
          text={truncateTextWithSearchTerm(
            result.question.title,
            searchTerm,
            200,
          )}
          searchTerm={searchTerm}
          className="text-sm text-gray-700 font-semibold"
        />
        <HighlightedText
          text={truncateTextWithSearchTerm(
            result.question.body,
            searchTerm,
            120,
          )}
          searchTerm={searchTerm}
          className="text-sm text-gray-500"
        />
      </div>
    </div>
  );
}

function SearchResultQuestionAnswer(props: Props) {
  const { result, searchTerm } = props;

  if (!result || !result.questionAnswer) {
    return null;
  }

  return (
    <div className="flex items-start py-2">
      <SearchResultCategory category="answer" />
      <div className="ml-3">
        <ResultItemHeaderWrapper>
          <div className="items-center flex gap-x-1">
            <MessageCirclePlusIcon className="w-3 h-3 text-gray-500" />
            <p className="text-xs/none text-gray-500">Answer</p>
          </div>
          <SeparationDot />
          <p className="text-xs/none text-gray-500">
            Submitted{" "}
            {formatDistanceToNowStrict(
              fromUnixTime(result.questionAnswer.createdAt),
              {
                addSuffix: true,
              },
            )}{" "}
            by {result.questionAnswer.answeredBy.name}
          </p>
        </ResultItemHeaderWrapper>
        <HighlightedText
          text={result.questionAnswer.answer}
          searchTerm={searchTerm}
          className="text-sm text-gray-700 font-semibold"
        />
      </div>
    </div>
  );
}

function SearchResultQuestionAnswerFile(props: Props) {
  const { result, searchTerm } = props;

  if (!result || !result.questionAnswer || !result.dataRoomFile) {
    return null;
  }

  return (
    <div className="flex items-start py-2">
      <SearchResultCategory category="answer_file" />
      <div className="ml-3">
        <ResultItemHeaderWrapper>
          <div className="items-center flex gap-x-1">
            <FilePlusIcon className="w-3 h-3 text-gray-500" />
            <p className="text-xs/none text-gray-500">
              Attached for answer to{" "}
              <span className="font-semibold">
                {truncateText(result.questionAnswer.question.title, 70)}
              </span>
            </p>
          </div>
          <SeparationDot />
          <p className="text-xs/none text-gray-500">
            Submitted{" "}
            {formatDistanceToNowStrict(
              fromUnixTime(result.questionAnswer.createdAt),
              {
                addSuffix: true,
              },
            )}{" "}
            by {result.questionAnswer.answeredBy.name}
          </p>
        </ResultItemHeaderWrapper>
        <div className="flex items-center gap-2">
          <FileIcon fileType={result.dataRoomFile.fileType} />
          <HighlightedText
            text={result.dataRoomFile.name}
            searchTerm={searchTerm}
            className="text-sm text-gray-700 font-semibold"
          />
        </div>
      </div>
    </div>
  );
}

function SearchResultDdql(props: Props) {
  const { result, searchTerm } = props;

  if (!result || !result.ddql) {
    return null;
  }

  return (
    <div className="flex items-start w-full py-2">
      <SearchResultCategory category="ddql" />
      <div className="ml-3">
        <ResultItemHeaderWrapper>
          <div className="items-center flex gap-x-1">
            <ListIcon className="w-3 h-3 text-gray-500" />
            <p className="text-xs/none text-gray-500">Question list</p>
          </div>
          <SeparationDot />
          <p className="text-xs/none text-gray-500">
            Created{" "}
            {formatDistanceToNowStrict(fromUnixTime(result.ddql.createdAt), {
              addSuffix: true,
            })}{" "}
            by {result.ddql.createdBy.name}
          </p>
        </ResultItemHeaderWrapper>
        <HighlightedText
          text={result.ddql.name}
          searchTerm={searchTerm}
          className="text-sm text-gray-700 font-semibold"
        />
      </div>
    </div>
  );
}

function SearchResultDealAccount(props: Props) {
  const { result, searchTerm } = props;

  if (!result || !result.dealAccount) {
    return null;
  }

  return (
    <div className="flex items-start py-2">
      <SearchResultCategory category="access" />
      <div className="ml-3">
        <ResultItemHeaderWrapper>
          {result.dealAccount.dealGroup ? (
            <>
              <p className="text-xs/none text-gray-500">
                {result.dealAccount.dealGroup.name}
              </p>
              <SeparationDot />
            </>
          ) : null}
          <p className="text-xs/none text-gray-500">
            Joined{" "}
            {formatDistanceToNowStrict(
              fromUnixTime(result.dealAccount.createdAt),
              {
                addSuffix: true,
              },
            )}
          </p>
        </ResultItemHeaderWrapper>
        <div className="flex items-center gap-2">
          <Avatar account={result.dealAccount.account} />
          <div>
            <HighlightedText
              text={result.dealAccount.account.name}
              searchTerm={searchTerm}
              className="text-sm text-gray-700 font-semibold"
            />
            <HighlightedText
              text={result.dealAccount.account.email}
              searchTerm={searchTerm}
              className="text-sm text-gray-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
